import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useTenant } from '../contexts/TenantContext'

export const useSponsors = (tournamentId) => {
    const { activeTenant } = useTenant()
    const [sponsors, setSponsors] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (tournamentId) {
            fetchSponsors()

            // Realtime subscription for sponsors
            const channel = supabase
                .channel(`sponsors_live_${tournamentId}`)
                .on('postgres_changes', {
                    event: '*',
                    schema: 'public',
                    table: 'sponsors',
                    filter: `tournament_id=eq.${tournamentId}`
                }, () => {
                    console.log('⚡ Realtime Sponsor Update Detected! Refetching...')
                    fetchSponsors()
                })
                .subscribe()

            return () => {
                supabase.removeChannel(channel)
            }
        }
    }, [tournamentId])

    const fetchSponsors = async () => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('sponsors')
                .select('*')
                .eq('tournament_id', tournamentId)
                .order('created_at', { ascending: false })

            if (error) throw error
            setSponsors(data || [])
        } catch (err) {
            console.error('Error fetching sponsors:', err)
        } finally {
            setLoading(false)
        }
    }

    const createSponsor = async (sponsorData) => {
        const { data, error } = await supabase
            .from('sponsors')
            .insert([{
                ...sponsorData,
                tournament_id: tournamentId,
                tenant_id: activeTenant?.id || null
            }])
            .select()
            .single()

        if (error) throw error
        setSponsors(prev => [data, ...prev])
        return data
    }

    const updateSponsor = async (sponsorId, updates) => {
        const { data, error } = await supabase
            .from('sponsors')
            .update(updates)
            .eq('id', sponsorId)
            .select()
            .single()

        if (error) throw error
        setSponsors(prev => prev.map(s => s.id === sponsorId ? data : s))
        return data
    }

    const deleteSponsor = async (sponsorId) => {
        const { error } = await supabase
            .from('sponsors')
            .delete()
            .eq('id', sponsorId)

        if (error) throw error
        setSponsors(prev => prev.filter(s => s.id !== sponsorId))
    }

    return {
        sponsors,
        loading,
        createSponsor,
        updateSponsor,
        deleteSponsor,
        refetch: fetchSponsors
    }
}
